import styles from 'css/src/components/app/upload-modal/FileInputUI.module.scss';

import React from 'react';
import classNames from 'classnames';

import type { AssetTypes } from 'js/src/client';
import { UploadFileSizeRequirementsPretty } from 'js/src/client';
import type { FilesSelectedType } from 'js/src/components/app/upload-modal/upload-modal-types';
import { PrimaryButton } from 'js/src/components/common/ui/Button';
import FileInput from 'js/src/components/common/ui/file-input';
import { getUploadableFormatsMessage } from 'js/src/libs/consts';

type FileInputUIPresentationalProps = {
    fileRequirements: string;
    isMiniature?: boolean;
};

function FileInputUIPresentational({
    fileRequirements,
    isMiniature,
}: FileInputUIPresentationalProps) {
    if (isMiniature) {
        return (
            <div className={styles['display-text']}>
                <i className={`material-symbols-outlined ${styles['add-icon']}`}>add</i>
            </div>
        );
    }

    return (
        <div className="text-center position-absolute top-50 start-50 translate-middle pe-none">
            <h4>Drag and drop files here</h4>
            <PrimaryButton text="Upload files" className="my-l5-16" />
            <p className="font-l5 font-l5--subtitle-l mb-l5-4">
                {`File Requirements: ${fileRequirements}`}
            </p>
            <p className="font-l5 font-l5--subtitle-l mb-l5-4">
                Max {UploadFileSizeRequirementsPretty.MAX_BYTES}
            </p>
        </div>
    );
}

type FileInputUIProps = {
    filesSelected: FilesSelectedType;
    isMiniature?: boolean;
    disableMultipleFileUpload?: boolean;
    acceptAssetTypes: AssetTypes[];
    acceptMimeTypes?: string;
    'data-testid'?: string;
};

const FileInputUI = React.memo(function FileInputUI({
    filesSelected,
    isMiniature = false,
    disableMultipleFileUpload = false,
    acceptAssetTypes,
    acceptMimeTypes,
    'data-testid': dataTestid,
}: FileInputUIProps) {
    const [isDragging, setIsDragging] = React.useState(false);

    const dragEnter = React.useCallback(() => {
        setIsDragging(true);
    }, []);

    const dragLeave = React.useCallback(() => {
        setIsDragging(false);
    }, []);

    const onChange = React.useCallback(
        (files: File[]) => {
            setIsDragging(false);
            // define the default tracker
            const uploadTypeTracker = {
                uploadingAgent: `${isDragging ? 'Drag-N-Drop' : 'File selection'} Media-Modal Asset Uploader}`,
                activity: 'Uploading Media',
                type: acceptAssetTypes,
            };
            // logging event for analytics
            analytics.track('Media Upload Type Tracker', uploadTypeTracker);
            const selectedFiles = disableMultipleFileUpload ? [files[0]] : files;
            filesSelected(selectedFiles.filter((file) => !!file));
        },
        [disableMultipleFileUpload, filesSelected, isDragging, acceptAssetTypes],
    );

    const fileRequirements = getUploadableFormatsMessage(acceptAssetTypes, acceptMimeTypes);

    return (
        <form
            data-testid="file-input-ui"
            className={classNames(styles['file-input-ui'], 'form-vertical', {
                [styles['miniature-upload']]: isMiniature,
                [styles['single-file-upload']]: isMiniature && disableMultipleFileUpload,
                [styles['styled-upload-form']]: !isMiniature,
            })}
        >
            <div className="upload-form h-100 position-relative">
                <div
                    className={classNames(styles['styled-upload'], {
                        [styles['drag-hover']]: isDragging,
                    })}
                >
                    <FileInput
                        onDragEnter={dragEnter}
                        onDragLeave={dragLeave}
                        onChange={onChange}
                        disableMultipleFileUpload={disableMultipleFileUpload}
                        data-testid={dataTestid}
                        accept={acceptMimeTypes}
                    />
                    <FileInputUIPresentational
                        fileRequirements={fileRequirements}
                        isMiniature={isMiniature}
                    />
                </div>
            </div>
        </form>
    );
});

export default FileInputUI;
